import { EXPENSE_STATUSES, labelOf, type Expense } from "@/api/finance";
import { todayIso } from "@/lib/dates";

/* Wording and tone for the tracker's bill flags: the renewal alert and the status. */

export type Tone = "destructive" | "warning" | "muted" | "secondary";

/** The renewal badge for a recurring bill; null when there is nothing to say. */
export function renewalBadge(
  expense: Pick<Expense, "renewal" | "next_renewal">,
  today = todayIso(),
): { tone: Tone; label: string } | null {
  if (!expense.renewal || !expense.next_renewal) return null;
  if (expense.renewal === "renewed") return { tone: "muted", label: "Renewed" };
  if (expense.renewal === "overdue") return { tone: "destructive", label: "Overdue for renewal" };
  if (expense.renewal === "due_soon") {
    const days = Math.round((Date.parse(expense.next_renewal) - Date.parse(today)) / 86_400_000);
    return { tone: "warning", label: days <= 0 ? "Renews today" : `Renews in ${days} days` };
  }
  return null;
}

/** The status badge; nothing for a bill the company has paid. */
export function statusBadge(
  expense: Pick<Expense, "status">,
): { tone: Tone; label: string } | null {
  if (expense.status === "unpaid") return { tone: "destructive", label: "Unpaid" };
  if (expense.status === "to_reimburse") return { tone: "warning", label: "To reimburse" };
  return null;
}

export function statusLabel(status: Expense["status"]): string {
  return labelOf(EXPENSE_STATUSES, status);
}
